import type { HttpClient } from "@/shared/api/http";

export type RenderPrintRequest = {
  docCode: string;
  entityType?: string;
  entityKey?: string;
  format?: "html" | "pdf";
  archive?: boolean;
  layoutJson?: string | null;
};

export type RenderPrintResponse = {
  html: string;
  contentType: string;
  objectKey: string | null;
  readUri: string | null;
  pdfObjectKey?: string | null;
  downloadPath?: string | null;
  fileName?: string | null;
};

export type PrintCatalogItem = {
  docCode: string;
  titleVi: string;
  docKind: string;
  binding: {
    bindingVersion: number;
    entityType: string;
    entityKeyField: string;
    entityFilter: Record<string, string> | null;
    lineSource: string;
    statusGate: string[] | null;
    permissions: string[] | null;
    sampleEntityKey: string | null;
  } | null;
  effectiveVersionNo: number;
  sampleEntityKey: string | null;
};

export async function renderPrintDocument(
  http: HttpClient,
  request: RenderPrintRequest,
): Promise<RenderPrintResponse> {
  return http.request<RenderPrintResponse>("/api/v1/doc/print/render", {
    method: "POST",
    body: request,
  });
}

export async function listPrintCatalog(
  http: HttpClient,
  docKind?: string,
): Promise<PrintCatalogItem[]> {
  const q = docKind ? `?docKind=${encodeURIComponent(docKind)}` : "";
  return http.request<PrintCatalogItem[]>(`/api/v1/doc/print/catalog${q}`);
}

/** Canonical doc codes — PRT-1 WMS */
export const PRT_DOC_GR = "TM-WH-BM-07.01";
export const PRT_DOC_GI = "TM-WH-BM-07.02";
/** E5 — Count / Disposal / Move (TRANSFER) thin vouchers */
export const PRT_DOC_COUNT = "TM-WH-BM-07.03";
export const PRT_DOC_DISPOSAL = "TM-WH-BM-07.04";
export const PRT_DOC_TRANSFER = "TM-WH-BM-07.06";

/** PRT-2 SAL / PUR vouchers */
export const PRT_DOC_SO = "TM-SAL-SO-01.01";
export const PRT_DOC_PO = "TM-TMP-PU-01.01";

export function printDocumentPath(docCode: string, entityKey: string): string {
  return `/print/${encodeURIComponent(docCode)}/${encodeURIComponent(entityKey)}`;
}

export function legacyGrPrintPath(txnNo: string): string {
  return printDocumentPath(PRT_DOC_GR, txnNo);
}

export function legacyGiPrintPath(txnNo: string): string {
  return printDocumentPath(PRT_DOC_GI, txnNo);
}

export function transferPrintPath(txnNo: string): string {
  return printDocumentPath(PRT_DOC_TRANSFER, txnNo);
}

export function countPrintPath(countNo: string): string {
  return printDocumentPath(PRT_DOC_COUNT, countNo);
}

export function disposalPrintPath(disposalNo: string): string {
  return printDocumentPath(PRT_DOC_DISPOSAL, disposalNo);
}

export function soPrintPath(orderNo: string): string {
  return printDocumentPath(PRT_DOC_SO, orderNo);
}

export function poPrintPath(orderNo: string): string {
  return printDocumentPath(PRT_DOC_PO, orderNo);
}

/** PRT-3 QMS IQC voucher */
export const PRT_DOC_IQC = "TM-QC-BM-04.01";

export function iqcPrintPath(inspectionOrLotKey: string): string {
  return printDocumentPath(PRT_DOC_IQC, inspectionOrLotKey);
}

/** PRT-4 packing list */
export const PRT_DOC_PACK = "TM-TMP-SA-01.02";

export function packingListPrintPath(shipmentNo: string): string {
  return printDocumentPath(PRT_DOC_PACK, shipmentNo);
}
